import { useAuthenticator } from '@aws-amplify/ui-react'
import { createContext, useEffect, useMemo, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { IUser } from '../../../../../app/entities/User'
import { RequireAuth } from '../../../global/components/auth/RequireAuth'
import { fetchData } from '../../../global/helper-functions/fetch'
import linksData from '../../../global/tsx/links'
import NavLinkPropsWithElement from '../../../global/types/NavLinkPropsWithElement'
import JmulletTools from '../../../jmullet-tools/components/JmulletTools'
import { Login } from '../../../login/Login'
import ReportsMain from '../../../reports/components/main/ReportsMain'
import AppointmentsMain from '../../../schedule/components/main/AppointmentsMain'
import WorkOrdersMain from '../../../work-orders/components/main/WorkOrdersMain'
import ModalContent from '../../../work-orders/components/work-order-detail/components/main/ModalContent'
import NavDrawer from '../nav-drawer/NavDrawer'
import { getUserPermissions } from './api'
import styles from './styles.module.css'
import VansMain from '../../../vans/main/VansMain'

interface IUserContext {
  user: IUser | null
  refetchUser: () => void
}

export const UserContext = createContext<IUserContext>({
  user: null,
  refetchUser: () => {},
})

function BaseContainer() {
  const { user } = useAuthenticator((context) => [context.user])
  const [userRole, setUserRole] = useState(localStorage.getItem('Role'))
  const [userData, setUserData] = useState<IUser | null>(null)

  const links: Array<NavLinkPropsWithElement> = useMemo(
    () => linksData.filter((elem) => !elem.permissionLevels || elem.permissionLevels?.includes(userRole || '')),
    [userRole],
  )

  const { pathname } = useLocation()
  const routesNotDisplayingNavDrawer = [
    /^\/login(\/.*)?$/i,
    /^\/customer-tip-screen\/.+$/,
    /^\/customer-update-cc\/.+$/,
    /^\/schedule-appointment\/.+$/,
  ]
  const shouldDisplayNavDrawer = !routesNotDisplayingNavDrawer.some((route) => route.test(pathname))

  const refetchUser = () => {
    if (user.challengeName === 'NEW_PASSWORD_REQUIRED' && !(user as any)?.signInUserSession?.idToken) {
      return
    }
    const email = (user as any)?.signInUserSession?.idToken?.payload?.email ?? ''

    fetchData<IUser>(getUserPermissions(email)).then((userInfo) => {
      setUserData(userInfo)
      localStorage.setItem('Role', userInfo.role)
      setUserRole(userInfo.role)
    })
  }

  useEffect(() => {
    //fetch user role & save to local storage
    if (user) {
      refetchUser()
    }
  }, [user])

  return (
    <UserContext.Provider value={{ user: userData, refetchUser }}>
      <div className={styles.baseContainerWrapper}>
        {shouldDisplayNavDrawer && <NavDrawer links={links}></NavDrawer>}
        <Routes>
          <Route
            path={'/jmullet-tools'}
            element={
              <RequireAuth>
                <JmulletTools />
              </RequireAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppointmentsMain />
              </RequireAuth>
            }>
            <Route
              path=":id"
              element={<ModalContent />}
            />
          </Route>
          <Route
            path="work-orders"
            element={
              <RequireAuth>
                <WorkOrdersMain />
              </RequireAuth>
            }>
            <Route
              path=":id"
              element={<ModalContent />}
            />
          </Route>
          <Route
            path="/vans"
            element={
              <RequireAuth>
                <VansMain />
              </RequireAuth>
            }>
            <Route
              path=":id"
              element={
                <RequireAuth>
                  <VansMain />
                </RequireAuth>
              }
            />
          </Route>
          {links.map((link) => (
            <Route
              path={String(link.to)}
              element={link.element}
              key={link.title}
            />
          ))}
          <Route
            path="/reports"
            element={
              <RequireAuth>
                <ReportsMain />
              </RequireAuth>
            }>
            <Route
              path=":id"
              element={<ModalContent />}
            />
          </Route>
          <Route
            path={'/login/*'}
            element={<Login />}
          />
        </Routes>
      </div>
    </UserContext.Provider>
  )
}

export default BaseContainer
